import React from 'react';
import { Avatar, Box, Button, Chip, Rating, Typography } from '@mui/material';
import { Star } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { DOCUMENT, documentEyebrowSx } from '../constants/documentTheme';
import { specialtyLabel } from '../constants/advisorDirectory';
import { partnerOxbloodBtnSx } from './PartnerListChrome';

const AdvisorDirectoryCard = ({
  advisor,
  onContact,
  onView,
  testId,
}) => {
  const { t } = useTranslation();
  const specialtyText = (advisor.specialties || [])
    .slice(0, 4)
    .map((item) => specialtyLabel(t, item))
    .join(' · ');
  const ratingCount = Number(advisor.ratingSummary?.count) || 0;
  const ratingAverage = Number(advisor.ratingSummary?.average) || 0;

  return (
    <Box
      data-testid={testId}
      sx={{
        bgcolor: DOCUMENT.surface,
        border: `1px solid ${advisor.isFeatured ? DOCUMENT.accent : DOCUMENT.ruleHard}`,
        p: { xs: 2, md: 2.5 },
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        height: '100%',
        boxShadow: advisor.isFeatured ? `inset 0 0 0 1px ${DOCUMENT.accent}` : 'none',
      }}
    >
      <Box sx={{ display: 'flex', gap: 1.75, alignItems: 'flex-start' }}>
        {advisor.logo ? (
          <Box
            component="img"
            src={advisor.logo}
            alt=""
            sx={{ width: 56, height: 56, objectFit: 'contain', flexShrink: 0 }}
          />
        ) : (
          <Avatar
            sx={{
              width: 56,
              height: 56,
              bgcolor: DOCUMENT.oxblood,
              color: DOCUMENT.ivory,
              fontFamily: DOCUMENT.display,
            }}
          >
            {advisor.companyName?.charAt(0)}
          </Avatar>
        )}
        <Box sx={{ minWidth: 0, flexGrow: 1 }}>
          <Typography sx={{ fontSize: 15, fontWeight: 600, color: DOCUMENT.ink }}>
            {advisor.contactName}
          </Typography>
          <Typography sx={{ fontSize: 14, color: DOCUMENT.ink2, mt: 0.25 }}>
            {advisor.companyName}
          </Typography>
          {advisor.locationLabel ? (
            <Typography sx={{ fontSize: 12.5, color: DOCUMENT.ink3, mt: 0.75 }}>
              {advisor.locationLabel}
              {specialtyText ? ` · ${specialtyText}` : ''}
            </Typography>
          ) : specialtyText ? (
            <Typography sx={{ fontSize: 12.5, color: DOCUMENT.ink3, mt: 0.75 }}>
              {specialtyText}
            </Typography>
          ) : null}
        </Box>
      </Box>

      {advisor.description ? (
        <Typography sx={{ fontSize: 13.5, color: DOCUMENT.ink2, lineHeight: 1.55 }}>
          {advisor.description.length > 160
            ? `${advisor.description.slice(0, 160).trim()}…`
            : advisor.description}
        </Typography>
      ) : null}

      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
        {advisor.isVerifiedPartner ? (
          <Chip
            icon={<Star sx={{ fontSize: 14 }} />}
            label={t('advisorDirectory.verifiedPartner')}
            size="small"
            sx={{
              height: 24,
              fontSize: 10,
              letterSpacing: '0.08em',
              bgcolor: DOCUMENT.canvas,
              border: `1px solid ${DOCUMENT.rule}`,
              '& .MuiChip-label': { px: 1 },
            }}
          />
        ) : null}
        {advisor.isFeatured ? (
          <Typography sx={{ ...documentEyebrowSx, color: DOCUMENT.oxblood, fontSize: 10 }}>
            {t('advisorDirectory.featuredAgency')}
          </Typography>
        ) : null}
      </Box>

      {ratingCount > 0 ? (
        <Box
          sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}
          data-testid={`advisor-card-rating-${advisor.slug}`}
        >
          <Rating value={ratingAverage} precision={0.1} readOnly size="small" />
          <Typography sx={{ fontSize: 13, color: DOCUMENT.ink2 }}>
            {t('tripRating.cardAverage', {
              average: ratingAverage,
              count: ratingCount,
            })}
          </Typography>
        </Box>
      ) : null}

      <Box sx={{ display: 'flex', gap: 1.25, mt: 'auto', pt: 0.5 }}>
        <Button
          onClick={() => onContact?.(advisor)}
          sx={{ ...partnerOxbloodBtnSx, flex: 1 }}
          data-testid={`advisor-contact-${advisor.slug}`}
        >
          {t('advisorDirectory.contactAdvisor')}
        </Button>
        <Button
          onClick={() => onView?.(advisor)}
          sx={{
            flex: 1,
            border: `1px solid ${DOCUMENT.ruleHard}`,
            color: DOCUMENT.ink,
            borderRadius: 0,
            textTransform: 'uppercase',
            letterSpacing: '0.1em',
            fontSize: 11,
            boxShadow: 'none',
            '&:hover': { borderColor: DOCUMENT.accent, bgcolor: DOCUMENT.canvas, boxShadow: 'none' },
          }}
        >
          {t('advisorDirectory.viewProfile')}
        </Button>
      </Box>
    </Box>
  );
};

export default AdvisorDirectoryCard;
